import React, { useState } from 'react';
import { Customer } from '../../types';
import { createCustomer, updateCustomer } from '../../services/api';

interface Props {
  customer?: Customer | null;
  onSaved: (c: Customer) => void;
  onClose: () => void;
}

const CustomerForm: React.FC<Props> = ({ customer, onSaved, onClose }) => {
  const isEdit = !!customer;

  const [form, setForm] = useState({
    name:    customer?.name    ?? '',
    email:   customer?.email   ?? '',
    phone:   customer?.phone   ?? '',
    company: customer?.company ?? '',
    address: customer?.address ?? '',
    notes:   customer?.notes   ?? '',
  });
  const [errors, setErrors]   = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const handle = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setErrors(errs => ({ ...errs, [e.target.name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setErrors({});
    try {
      const saved = isEdit && customer
        ? await updateCustomer(customer.id, form)
        : await createCustomer(form);
      onSaved(saved);
    } catch (err: any) {
      if (err?.response?.data?.errors) {
        const mapped: Record<string, string> = {};
        Object.entries(err.response.data.errors).forEach(([k, v]) => { mapped[k] = (v as string[])[0]; });
        setErrors(mapped);
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-backdrop" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="deal-modal">
        <div className="deal-modal-header" style={{ borderTopColor: '#3b82f6' }}>
          <div className="deal-modal-header-inner">
            <div>
              <h2 className="deal-modal-title">{isEdit ? 'Edit Customer' : 'New Customer'}</h2>
              <p className="deal-modal-subtitle">{isEdit ? 'Update contact details' : 'Add a new customer to your CRM'}</p>
            </div>
            <button className="deal-modal-close" onClick={onClose}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="deal-modal-body">
          <div className="deal-form-row">
            <div className="deal-form-group">
              <label className="deal-form-label">Full Name <span className="required">*</span></label>
              <input className={`deal-form-input${errors.name ? ' input-error' : ''}`} name="name" value={form.name} onChange={handle} placeholder="John Doe" required />
              {errors.name && <span className="field-error">{errors.name}</span>}
            </div>
            <div className="deal-form-group">
              <label className="deal-form-label">Email <span className="required">*</span></label>
              <input className={`deal-form-input${errors.email ? ' input-error' : ''}`} name="email" type="email" value={form.email} onChange={handle} placeholder="john@company.com" required />
              {errors.email && <span className="field-error">{errors.email}</span>}
            </div>
          </div>

          <div className="deal-form-row">
            <div className="deal-form-group">
              <label className="deal-form-label">Phone</label>
              <input className="deal-form-input" name="phone" type="tel" value={form.phone} onChange={handle} placeholder="+1 555 000 0000" />
            </div>
            <div className="deal-form-group">
              <label className="deal-form-label">Company</label>
              <input className="deal-form-input" name="company" value={form.company} onChange={handle} placeholder="Company name" />
            </div>
          </div>

          <div className="deal-form-group">
            <label className="deal-form-label">Address</label>
            <input className="deal-form-input" name="address" value={form.address} onChange={handle} placeholder="Street, City, Country" />
          </div>

          <div className="deal-form-group">
            <label className="deal-form-label">Notes</label>
            <textarea className="deal-form-input" name="notes" value={form.notes} onChange={handle} rows={3} placeholder="Any additional context…" />
          </div>

          <div className="deal-modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <><span className="btn-spinner" />Saving…</> : isEdit ? 'Save Changes' : 'Create Customer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerForm;
